import React, { useEffect, useState } from "react";
import TopNav from "@/components/TopNav";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    ShieldCheck, Users, Send, RefreshCw, ShieldAlert, CheckCircle2, XCircle, BarChart3,
} from "lucide-react";
import { api } from "@/lib/api";
import AdminAnalyticsPanel from "@/components/AdminAnalyticsPanel";
import AdminOperationsPanel from "@/components/AdminOperationsPanel";

const ROLES = ["super_admin", "admin", "coo", "regional_manager", "coordinator", "agency", "caregiver", "family"];

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [roleFilter, setRoleFilter] = useState("caregiver");
    const [inviteEmail, setInviteEmail] = useState("");
    const [busyId, setBusyId] = useState(null);

    async function loadAll() {
        try {
            const [s, u] = await Promise.all([
                api.get("/admin/stats"),
                api.get(`/admin/users${roleFilter ? `?role=${roleFilter}` : ""}`),
            ]);
            setStats(s.data); setUsers(u.data);
        } catch { /* noop */ }
    }
    useEffect(() => { loadAll(); }, [roleFilter]);

    async function verifyBadge(id, badge) {
        try { await api.post("/admin/verify-badge", { user_id: id, badge, verified: true }); toast.success(`${badge} verified`); loadAll(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    }

    async function promote(userId, newRole) {
        setBusyId(userId);
        try {
            let assignedStates = null;
            if (newRole === "regional_manager") {
                const raw = window.prompt(
                    "Regional Manager needs at least one US state code (e.g. MI or NY,CA,TX).\nEnter comma-separated 2-letter codes:",
                    ""
                );
                if (!raw) { setBusyId(null); return; }
                assignedStates = raw.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean);
                if (assignedStates.length === 0) {
                    toast.error("At least one state required");
                    setBusyId(null);
                    return;
                }
            }
            const { data } = await api.post("/admin/promote", {
                user_id: userId,
                role: newRole,
                assigned_states: assignedStates,
            });
            toast.success(
                `Role changed: ${data.before_role} → ${data.after_role}` +
                (data.assigned_states?.length ? ` (${data.assigned_states.join(", ")})` : "")
            );
            loadAll();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setBusyId(null);
        }
    }

    async function sendInvite() {
        if (!inviteEmail) return toast.error("Enter an email");
        try {
            const { data } = await api.post("/auth/coordinator-invite", { email: inviteEmail });
            toast.success("Invite sent (mocked email logged)", { description: `Code: ${data.code}` });
            setInviteEmail("");
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    }

    async function sweep(kind) {
        try {
            const { data } = await api.post(`/admin/sweep/${kind}`);
            toast.success(`${kind}: ${JSON.stringify(data)}`);
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    }

    return (
        <div className="min-h-screen bg-stone-50">
            <TopNav />
            <main data-testid="admin-dashboard" className="max-w-7xl mx-auto px-6 lg:px-8 py-10 animate-fade-in">
                <div className="mb-8">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-2">Platform admin</div>
                    <h1 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900 flex items-center gap-3">
                        <ShieldCheck className="w-7 h-7 text-emerald-700" /> Admin
                    </h1>
                </div>

                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                        <StatBox label="Total users" value={stats.total_users} />
                        <StatBox label="Care requests (open)" value={stats.open_care_requests} />
                        <StatBox label="Active visits" value={stats.active_visits} accent="emerald" />
                        <StatBox label="Completed visits" value={stats.completed_visits} />
                    </div>
                )}

                {/* Rich analytics: charts + KPIs */}
                <div className="mb-6">
                    <AdminAnalyticsPanel />
                </div>

                {/* Operations Center: B2B / transactions / payments due / jobs / marketing / comms */}
                <div className="mb-6">
                    <AdminOperationsPanel />
                </div>

                <div className="grid lg:grid-cols-3 gap-6 mb-6">
                    <Card className="rounded-2xl border-stone-200 lg:col-span-2">                        <CardContent className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-outfit text-xl font-semibold text-stone-900 flex items-center gap-2">
                                    <Users className="w-5 h-5 text-emerald-700" /> Users
                                </h3>
                                <div className="flex gap-2">
                                    {["caregiver", "family", "agency", "regional_manager", "coordinator", "admin", "super_admin"].map((r) => (
                                        <button
                                            key={r}
                                            data-testid={`admin-role-filter-${r}`}
                                            onClick={() => setRoleFilter(r)}
                                            className={`px-3 py-1 rounded-full text-xs font-medium ${roleFilter === r ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-700"}`}
                                        >
                                            {r}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="space-y-2 max-h-[500px] overflow-y-auto">
                                {users.map((u) => (
                                    <div
                                        key={u.id}
                                        data-testid="admin-user-row"
                                        data-user-role={u.role}
                                        className="p-3 border border-stone-200 rounded-xl flex items-center justify-between gap-3 flex-wrap"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="font-medium text-stone-900 text-sm truncate">{u.full_name}</div>
                                            <div className="text-xs text-stone-500 font-dmsans truncate">
                                                {u.email}
                                                {u.credential && ` · ${u.credential}`}
                                                {u.role === "regional_manager" && u.assigned_states?.length > 0 && (
                                                    <span className="ml-1 inline-flex items-center gap-1 text-emerald-700">
                                                        · states: {u.assigned_states.join(", ")}
                                                    </span>
                                                )}
                                                {u.family_state && ` · ${u.family_state}`}
                                                {(u.verified_badges || []).length > 0 && ` · ${(u.verified_badges || []).join(", ")}`}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            {u.role === "caregiver" && (
                                                <div className="flex gap-1">
                                                    {["background_check", "cpr", "insurance"].map((b) => (
                                                        <Button
                                                            key={b}
                                                            data-testid={`admin-verify-${b}`}
                                                            size="sm"
                                                            variant={u.verified_badges?.includes(b) ? "default" : "outline"}
                                                            onClick={() => verifyBadge(u.id, b)}
                                                            className={`h-7 text-xs rounded-full ${u.verified_badges?.includes(b) ? "bg-emerald-700 hover:bg-emerald-800 text-white" : ""}`}
                                                        >
                                                            {b.replace("_", " ")}
                                                        </Button>
                                                    ))}
                                                </div>
                                            )}
                                            <Select
                                                value={u.role}
                                                disabled={busyId === u.id}
                                                onValueChange={(v) => promote(u.id, v)}
                                            >
                                                <SelectTrigger
                                                    data-testid="admin-role-select"
                                                    className="h-8 w-32 rounded-full text-xs bg-stone-50 border-stone-200"
                                                >
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {ROLES.map((r) => (
                                                        <SelectItem key={r} value={r} className="text-xs">
                                                            {r}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    <div className="space-y-4">
                        <Card className="rounded-2xl border-stone-200">
                            <CardContent className="p-6">
                                <h3 className="font-outfit text-lg font-semibold text-stone-900 mb-3">Invite a coordinator</h3>
                                <Input
                                    data-testid="admin-invite-email"
                                    type="email"
                                    value={inviteEmail}
                                    onChange={(e) => setInviteEmail(e.target.value)}
                                    placeholder="new-coord@example.com"
                                    className="h-11 rounded-xl mb-3"
                                />
                                <Button data-testid="admin-send-invite" onClick={sendInvite} className="w-full rounded-full bg-emerald-700 hover:bg-emerald-800 text-white h-11">
                                    <Send className="w-4 h-4 mr-2" /> Send invite
                                </Button>
                            </CardContent>
                        </Card>

                        <Card className="rounded-2xl border-stone-200">
                            <CardContent className="p-6 space-y-2">
                                <h3 className="font-outfit text-lg font-semibold text-stone-900">Sweeps</h3>
                                <Button data-testid="admin-sweep-expiring" onClick={() => sweep("expiring-docs?days=30")} className="w-full rounded-full bg-stone-900 hover:bg-stone-800 text-white h-10">
                                    <RefreshCw className="w-4 h-4 mr-2" /> Notify expiring docs (30d)
                                </Button>
                                <Button data-testid="admin-sweep-missed" onClick={() => sweep("missed-visits")} variant="outline" className="w-full rounded-full h-10">
                                    Flag missed visits
                                </Button>
                                <Button
                                    data-testid="admin-sweep-tiers"
                                    onClick={async () => {
                                        try {
                                            const { data } = await api.post("/evv/sweep/expiry-tiers");
                                            toast.success(`Tiers: ${JSON.stringify(data)}`);
                                        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                                    }}
                                    variant="outline"
                                    className="w-full rounded-full h-10"
                                >
                                    Tiered expiry alerts (30/14/7)
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                </div>

                <div className="mb-6">
                    <AuditLogPanel />
                </div>
            </main>
        </div>
    );
}

function StatBox({ label, value, accent = "stone" }) {
    const map = { stone: "text-stone-900", emerald: "text-emerald-700" };
    return (
        <Card className="rounded-2xl border-stone-200">
            <CardContent className="p-5">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">{label}</div>
                <div className={`font-outfit text-3xl font-semibold mt-1 ${map[accent]}`}>{value}</div>
            </CardContent>
        </Card>
    );
}

function AuditLogPanel() {
    const [rows, setRows] = React.useState([]);
    const [actionFilter, setActionFilter] = React.useState("");

    async function load() {
        try {
            const q = actionFilter ? `?action=${actionFilter}&limit=100` : "?limit=100";
            const { data } = await api.get(`/admin/audit-log${q}`);
            setRows(data);
        } catch { /* noop */ }
    }
    React.useEffect(() => { load(); }, [actionFilter]);

    const ACTION_FILTERS = [
        { value: "", label: "all" },
        { value: "auth.login.success", label: "logins" },
        { value: "auth.login.failure", label: "failed logins" },
        { value: "admin.promote", label: "role changes" },
        { value: "admin.verify_badge", label: "badge verify" },
        { value: "admin.verify_compliance", label: "doc verify" },
    ];

    return (
        <Card className="rounded-2xl border-stone-200">
            <CardContent className="p-6">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <h3 className="font-outfit text-lg font-semibold text-stone-900 flex items-center gap-2">
                        <ShieldAlert className="w-5 h-5 text-emerald-700" /> Security audit log
                    </h3>
                    <div className="flex flex-wrap gap-1">
                        {ACTION_FILTERS.map((f) => (
                            <button
                                key={f.value || "all"}
                                data-testid={`admin-audit-filter-${f.value || "all"}`}
                                onClick={() => setActionFilter(f.value)}
                                className={`px-2.5 py-1 rounded-full text-[10px] font-medium ${
                                    actionFilter === f.value
                                        ? "bg-stone-900 text-white"
                                        : "bg-stone-100 text-stone-700 hover:bg-stone-200"
                                }`}
                            >
                                {f.label}
                            </button>
                        ))}
                        <button
                            onClick={load}
                            data-testid="admin-audit-refresh"
                            className="px-2.5 py-1 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                        >
                            refresh
                        </button>
                    </div>
                </div>
                <div className="space-y-1 max-h-[420px] overflow-y-auto">
                    {rows.length === 0 && (
                        <p className="text-xs text-stone-500 font-dmsans">No audit rows.</p>
                    )}
                    {rows.map((r) => {
                        const isFail = r.result === "failure" || r.action === "auth.login.failure";
                        const isAdmin = r.action?.startsWith("admin.");
                        return (
                            <div
                                key={r.id}
                                data-testid="admin-audit-row"
                                data-action={r.action}
                                className={`text-xs p-2.5 border rounded-lg flex items-start justify-between gap-2 font-dmsans ${
                                    isFail
                                        ? "border-red-100 bg-red-50/40"
                                        : isAdmin
                                            ? "border-amber-100 bg-amber-50/30"
                                            : "border-stone-100 bg-white"
                                }`}
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5">
                                        {isFail
                                            ? <XCircle className="w-3.5 h-3.5 text-red-600 shrink-0" />
                                            : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />}
                                        <span className="font-medium text-stone-900 truncate">{r.action}</span>
                                        {r.result && r.result !== "success" && (
                                            <span className="text-[10px] uppercase font-bold tracking-wider text-red-700">
                                                {r.result}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-stone-500 truncate mt-0.5 pl-5">
                                        {r.actor_email || r.actor_id || "anonymous"}
                                        {r.actor_role && ` · ${r.actor_role}`}
                                        {r.actor_ip && ` · ${r.actor_ip}`}
                                        {r.meta?.reason && ` · ${r.meta.reason}`}
                                        {r.meta?.before_role && r.meta?.after_role &&
                                            ` · ${r.meta.before_role} → ${r.meta.after_role}`}
                                        {r.meta?.badge && ` · ${r.meta.badge}`}
                                    </div>
                                </div>
                                <span className="text-stone-400 shrink-0 text-[10px]">
                                    {new Date(r.created_at).toLocaleTimeString()}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </CardContent>
        </Card>
    );
}
